import axios from 'axios';
import { normalizarIdentificacionFiscal, validarIdentificacionFiscal } from '../validarIdentificacionFiscal';

// Detecta el entorno y configura la URL de la API dinámicamente
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
export const SITE_URL = isLocal ? 'http://localhost/aplicacion1' : window.location.origin;
export const API_URL = `${SITE_URL}/api`;
export const UPLOADS_URL = `${SITE_URL}/uploads`;

export const obtenerUrlArchivo = (ruta) =>
  `${SITE_URL}/${String(ruta).replace(/^\/+/, '').split('/').map(encodeURIComponent).join('/')}`;

export const obtenerUrlImagenAviso = (nombreArchivo) =>
  obtenerUrlArchivo(`uploads/avisos/${nombreArchivo}`);

console.log(`Entorno detectado: ${isLocal ? 'Local' : 'Producción'}. URL de API: ${API_URL}`);

// Crear una instancia de axios para poder interceptar las respuestas
const apiClient = axios.create({
  baseURL: API_URL,
});

// Interceptor para añadir el token a las peticiones
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  try {
    const usuario = JSON.parse(localStorage.getItem('usuario') || 'null');
    const tecnicoId = localStorage.getItem('filtro_tecnico_id');
    if (usuario?.rol === 'administrador' && tecnicoId) {
      config.params = { ...(config.params || {}), tecnico_id: tecnicoId };
    }
  } catch (error) {
    console.error('No se pudo leer el filtro global de técnico:', error);
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Interceptor para manejar errores de autenticación de forma centralizada
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Token inválido o expirado
      localStorage.removeItem('token');
      localStorage.removeItem('usuario');
      // Redirigir al login
      window.location.href = '/iniciar-sesion';
    }
    return Promise.reject(error);
  }
);

/**
 * Obtiene una lista de avisos filtrando por estado.
 * @param {string} estado - El estado de los avisos a obtener (ej. 'pendiente', 'completado').
 * @returns {Promise<object>} La respuesta de la API.
 */
export const obtenerAvisos = async (estado) => {
  try {
    const response = await apiClient.get(`/obtener_avisos.php`, {
      params: { estado },
    });
    return response.data;
  } catch (error) {
    console.error(`Error al obtener los avisos con estado ${estado}:`, error);
    throw error;
  }
};

export const obtenerAvisosConFiltros = async (filtros = {}) => {
  try {
    const response = await apiClient.get('/obtener_avisos.php', { params: filtros });
    return response.data;
  } catch (error) {
    console.error('Error al obtener avisos con filtros:', error);
    throw error;
  }
};

/**
 * Busca un cliente y recupera su historial por teléfono o dirección.
 * @param {string} termino - Número de teléfono o dirección a buscar.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const buscarClienteDetalles = async (termino) => {
  try {
    const response = await apiClient.get('/buscar_cliente_detalles.php', {
      params: { termino },
    });
    return response.data;
  } catch (error) {
    console.error('Error al buscar los detalles del cliente:', error);
    throw error;
  }
};

/**
 * Obtiene todas las facturas registradas.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const obtenerFacturas = async (periodo) => {
  try {
    const params = periodo ? { periodo } : {};
    const response = await apiClient.get(`/obtener_facturas.php`, { params });
    return response.data;
  } catch (error) {
    console.error('Error al obtener las facturas:', error);
    throw error;
  }
};

/**
 * Crea una nueva factura en la base de datos.
 * @param {object} facturaData - Los datos de la factura a crear.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const crearFactura = async (facturaData) => {
  try {
    const response = await apiClient.post(`/crear_factura.php`, facturaData);
    return response.data;
  } catch (error) {
    console.error('Error al crear la factura:', error);
    // Lanza el error para que el componente que llama pueda manejarlo
    throw error;
  }
};

/**
 * Obtiene los detalles completos de una factura, incluyendo sus conceptos.
 * @param {number} idFactura - El ID de la factura a obtener.
 * @returns {Promise<object>} La respuesta de la API con los detalles de la factura.
 */
export const obtenerFacturaDetalle = async (idFactura) => {
  try {
    const response = await apiClient.get(`/obtener_factura_detalle.php`, {
      params: { id: idFactura },
    });
    return response.data;
  } catch (error) {
    console.error(`Error al obtener los detalles de la factura ${idFactura}:`, error);
    throw error;
  }
};

/**
 * Obtiene todos los productos del inventario.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const obtenerProductos = async () => {
  try {
    const response = await apiClient.get(`/obtener_productos.php`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener productos:', error);
    throw error;
  }
};

export const obtenerClavePublicaPush = async () => {
  const response = await apiClient.get('/suscripciones_push.php');
  return response.data;
};

export const guardarSuscripcionPush = async (suscripcion) => {
  const response = await apiClient.post('/suscripciones_push.php', {
    accion: 'guardar',
    suscripcion,
  });
  return response.data;
};

export const eliminarSuscripcionPush = async (suscripcion) => {
  const response = await apiClient.post('/suscripciones_push.php', {
    accion: 'eliminar',
    suscripcion,
  });
  return response.data;
};

/**
 * Agrega un nuevo producto al inventario.
 * @param {object} productData - Los datos del producto a agregar.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const agregarProducto = async (productData) => {
  try {
    const response = await apiClient.post(`/agregar_producto.php`, productData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error al agregar producto:', error);
    throw error;
  }
};

/**
 * Gestiona un movimiento (creación o actualización).
 * @param {FormData} movementData - Los datos del movimiento. Debe incluir 'tipo_movimiento', 'monto', 'concepto', 'fecha', etc.
 *                                  Si es una actualización, debe incluir 'id_movimiento'.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const gestionarMovimiento = async (movementData) => {
  try {
    const response = await apiClient.post(`/registrar_movimiento.php`, movementData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error al gestionar movimiento:', error);
    throw error;
  }
};

/**
 * Registra un movimiento de stock (entrada o salida) y actualiza el inventario.
 * @param {object} stockMovementData - Los datos del movimiento de stock.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const registrarMovimientoStock = async (stockMovementData) => {
  try {
    const response = await apiClient.post(`/registrar_movimiento_stock.php`, stockMovementData);
    return response.data;
  } catch (error) {
    console.error('Error al registrar movimiento de stock:', error);
    throw error;
  }
};

/**
 * Crea un nuevo cliente.
 * @param {object} clientData - Los datos del cliente a crear.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const crearCliente = async (clientData) => {
  const nif = normalizarIdentificacionFiscal(clientData.nif ?? clientData.nif_cif);
  if (!validarIdentificacionFiscal(nif)) {
    return { exito: false, mensaje: 'Introduce un NIF, NIE o CIF válido.' };
  }

  try {
    const response = await apiClient.post(`/gestionar_cliente.php`, {
      ...clientData,
      nif,
      nif_cif: nif,
    });
    return response.data;
  } catch (error) {
    console.error('Error al crear cliente:', error);
    throw error;
  }
};

/**
 * Crea un nuevo aviso.
 * @param {FormData} avisoData - Los datos del aviso a crear, incluyendo archivos.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const crearAviso = async (avisoData) => {
  try {
    const response = await apiClient.post(`/crear_aviso.php`, avisoData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error al crear el aviso:', error);
    throw error;
  }
};

/**
 * Crea un registro de diagnóstico o reparación con sus imágenes adjuntas.
 * @param {object} registroData - Datos del cliente y del servicio.
 * @param {File[]} imagenes - Imágenes seleccionadas en el formulario.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const crearRegistroServicio = async (registroData, imagenes = []) => {
  const formData = new FormData();

  Object.entries(registroData).forEach(([campo, valor]) => {
    if (valor !== undefined && valor !== null && valor !== '') {
      formData.append(campo, String(valor));
    }
  });

  imagenes.forEach((imagen) => formData.append('imagenes[]', imagen));

  try {
    const response = await apiClient.post('/crear_registro_servicio.php', formData);
    return response.data;
  } catch (error) {
    console.error('Error al crear el registro de servicio:', error);
    throw error;
  }
};

/**
 * Obtiene las notificaciones pendientes de facturación.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const obtenerNotificacionesPendientes = async () => {
  try {
    const response = await apiClient.get('/obtener_notificaciones.php');
    return response.data;
  } catch (error) {
    console.error('Error al obtener las notificaciones pendientes:', error);
    throw error;
  }
};



/**
 * Registra un nuevo ingreso financiero.
 * @param {FormData} ingresoData - Los datos del ingreso a crear, incluyendo el justificante.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const crearIngreso = async (ingresoData) => {
  try {
    const response = await gestionarMovimiento(ingresoData); // Use gestionarMovimiento
    return response.data;
  } catch (error) {
    console.error('Error al registrar el ingreso:', error);
    throw error;
  }
};

/**
 * Actualiza un aviso existente.
 * @param {FormData} avisoData - Los datos del aviso a actualizar.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const actualizarAviso = async (avisoData) => {
  try {
    const response = await apiClient.post(`/actualizar_aviso.php`, avisoData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error al actualizar el aviso:', error);
    throw error;
  }
};

/**
 * Elimina un aviso por su ID.
 * @param {number} idAviso - El ID del aviso a eliminar.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const eliminarAviso = async (idAviso) => {
  try {
    const response = await apiClient.post(`/eliminar_aviso.php`, { id_aviso: idAviso });
    return response.data;
  } catch (error) {
    console.error('Error al eliminar el aviso:', error);
    throw error;
  }
};

/**
 * Crea un nuevo centro administrativo.
 * @param {object} centerData - Los datos del centro administrativo a crear.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const crearCentroAdministrativo = async (centerData) => {
  try {
    const response = await apiClient.post(`/gestionar_centros_administrativos.php`, centerData);
    return response.data;
  } catch (error) {
    console.error('Error al crear centro administrativo:', error);
    throw error;
  }
};

/**
 * Obtiene todos los centros administrativos.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const obtenerCentrosAdministrativos = async () => {
  try {
    const response = await apiClient.get(`/gestionar_centros_administrativos.php`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener centros administrativos:', error);
    throw error;
  }
};

/**
 * Descarga el PDF oficial (generado en el servidor, con el QR de verificación Veri*Factu)
 * de una factura ya emitida.
 * @param {number} idFactura - El ID de la factura.
 * @returns {Promise<Blob>} El contenido binario del PDF.
 */
export const descargarFacturaPdf = async (idFactura) => {
  try {
    const response = await apiClient.get(`/descargar_factura.php`, {
      params: { id: idFactura },
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error(`Error al descargar el PDF de la factura ${idFactura}:`, error);
    throw error;
  }
};

export const descargarAuditoriaFacturas = async () => {
  try {
    const response = await apiClient.get('/exportar_auditoria_facturas.php', {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Error al exportar los registros de auditoría de facturas:', error);
    throw error;
  }
};

/**
 * Genera una factura en formato Facturae XML.
 * @param {object} invoiceData - Los datos de la factura.
 * @returns {Promise<string>} El contenido XML de la facturae.
 */
export const generarFacturae = async (invoiceData) => {
  try {
    const response = await apiClient.post(`/generar_facturae.php`, invoiceData, {
      headers: {
        'Content-Type': 'application/json',
      },
      responseType: 'text', // Expect text response (XML)
    });
    return response.data;
  } catch (error) {
    console.error('Error al generar Facturae:', error);
    throw error;
  }
};

/**
 * Guarda la configuración de la empresa.
 * @param {object} configData - Los datos de configuración de la empresa.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const guardarConfiguracionEmpresa = async (configData) => {
  try {
    const response = await apiClient.post(`/gestionar_configuracion_empresa.php`, configData);
    return response.data;
  } catch (error) {
    console.error('Error al guardar la configuración de la empresa:', error);
    throw error;
  }
};

/**
 * Obtiene la configuración de la empresa.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const obtenerConfiguracionEmpresa = async () => {
  try {
    const response = await apiClient.get(`/gestionar_configuracion_empresa.php`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener la configuración de la empresa:', error);
    throw error;
  }
};

/**
 * Obtiene todos los clientes o busca clientes por término.
 * @param {string} [searchTerm] - Término de búsqueda para filtrar clientes por NIF o nombre.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const obtenerClientes = async (searchTerm = '') => {
  try {
    const response = await apiClient.get(`/obtener_clientes.php`, {
      params: { q: searchTerm }
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener clientes:', error);
    throw error;
  }
};

/**
 * Obtiene todas las notas de documento.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const obtenerNotas = async () => {
  try {
    const response = await apiClient.get(`/gestionar_notas.php`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener notas:', error);
    throw error;
  }
};

/**
 * Crea una nueva nota de documento.
 * @param {object} noteData - Los datos de la nota a crear (titulo, descripcion).
 * @returns {Promise<object>} La respuesta de la API.
 */
export const crearNota = async (noteData) => {
  try {
    const response = await apiClient.post(`/gestionar_notas.php`, noteData);
    return response.data;
  } catch (error) {
    console.error('Error al crear nota:', error);
    throw error;
  }
};

/**
 * Actualiza una nota de documento existente.
 * @param {object} noteData - Los datos de la nota a actualizar (id, titulo, descripcion).
 * @returns {Promise<object>} La respuesta de la API.
 */
export const actualizarNota = async (noteData) => {
  try {
    const response = await apiClient.post(`/gestionar_notas.php`, noteData); // POST for update
    return response.data;
  } catch (error) {
    console.error('Error al actualizar nota:', error);
    throw error;
  }
};

/**
 * Elimina una nota de documento por su ID.
 * @param {number} id - El ID de la nota a eliminar.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const eliminarNota = async (id) => {
  try {
    const response = await apiClient.delete(`/gestionar_notas.php`, { data: { id } }); // DELETE with data in body
    return response.data;
  } catch (error) {
    console.error('Error al eliminar nota:', error);
    throw error;
  }
};

/**
 * Obtiene el siguiente número de factura para una serie dada.
 * @param {number} serieId - El ID de la serie.
 * @returns {Promise<object>} La respuesta de la API con el siguiente número.
 */
export const obtenerSiguienteNumero = async (serieId) => {
  try {
    const response = await apiClient.get(`/obtener_siguiente_numero.php`, {
      params: { serie_id: serieId },
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener el siguiente número de factura:', error);
    throw error;
  }
};

/**
 * Obtiene todas las series de facturación.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const obtenerSeries = async () => {
  try {
    const response = await apiClient.get(`/gestionar_series.php`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener las series:', error);
    throw error;
  }
};

/**
 * Crea una nueva serie de facturación.
 * @param {object} serieData - Los datos de la serie a crear (nombre, mascara, tipo).
 * @returns {Promise<object>} La respuesta de la API.
 */
export const crearSerie = async (serieData) => {
  try {
    const response = await apiClient.post(`/gestionar_series.php`, serieData);
    return response.data;
  } catch (error) {
    console.error('Error al crear la serie:', error);
    throw error;
  }
};

/**
 * Elimina un cliente por su ID.
 * @param {number} idCliente - El ID del cliente a eliminar.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const eliminarCliente = async (idCliente) => {
  try {
    const response = await apiClient.post(`/eliminar_cliente.php`, { id_cliente: idCliente });
    return response.data;
  } catch (error) {
    console.error('Error al eliminar el cliente:', error);
    throw error;
  }
};

/**
 * Obtiene los detalles completos de un cliente por su ID.
 * @param {number} idCliente - El ID del cliente.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const obtenerClienteDetalle = async (idCliente) => {
  try {
    const response = await apiClient.get(`/obtener_cliente_detalle.php`, {
      params: { id: idCliente }
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener los detalles del cliente:', error);
    throw error;
  }
};

/**
 * Actualiza un cliente existente.
 * @param {number} idCliente - El ID del cliente a actualizar.
 * @param {object} clientData - Los nuevos datos del cliente.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const actualizarCliente = async (idCliente, clientData) => {
  const nif = normalizarIdentificacionFiscal(clientData.nif ?? clientData.nif_cif);
  if (!validarIdentificacionFiscal(nif)) {
    return { exito: false, mensaje: 'Introduce un NIF, NIE o CIF válido.' };
  }

  try {
    const response = await apiClient.post(`/gestionar_cliente.php`, { 
      id_cliente: idCliente, 
      ...clientData,
      nif,
      nif_cif: nif,
    });
    return response.data;
  } catch (error) {
    console.error('Error al actualizar el cliente:', error);
    throw error;
  }
};



/**
 * Obtiene todos los movimientos (ingresos y gastos).
 * @returns {Promise<object>} La respuesta de la API.
 */
export const obtenerMovimientos = async () => {
  try {
    const response = await apiClient.get(`/obtener_movimientos.php`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener movimientos:', error);
    throw error;
  }
};

/**
 * Elimina un movimiento por su ID.
 * @param {number} idMovimiento - El ID del movimiento a eliminar.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const eliminarMovimiento = async (idMovimiento) => {
  try {
    const response = await apiClient.post(`/eliminar_movimiento.php`, { id: idMovimiento });
    return response.data;
  } catch (error) {
    console.error('Error al eliminar el movimiento:', error);
    throw error;
  }
};

/**
 * Elimina un producto por su ID.
 * @param {number} idProducto - El ID del producto a eliminar.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const eliminarProducto = async (idProducto) => {
  try {
    const response = await apiClient.post(`/eliminar_producto.php`, { id_producto: idProducto });
    return response.data;
  } catch (error) {
    console.error('Error al eliminar el producto:', error);
    throw error;
  }
};

/**
 * Actualiza un producto existente.
 * @param {FormData} productData - Los datos del producto a actualizar, incluyendo el ID y opcionalmente una nueva imagen.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const actualizarProducto = async (productData) => {
  try {
    const response = await apiClient.post(`/actualizar_producto.php`, productData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error al actualizar el producto:', error);
    throw error;
  }
};

/**
 * Elimina una factura por su ID.
 * @param {number} idFactura - El ID de la factura a eliminar.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const eliminarFactura = async (idFactura) => {
  try {
    const response = await apiClient.post(`/eliminar_factura.php`, { id_factura: idFactura });
    return response.data;
  } catch (error) {
    console.error('Error al eliminar la factura:', error);
    throw error;
  }
};

/**
 * Actualiza el estado de una factura.
 * @param {number} idFactura - El ID de la factura a actualizar.
 * @param {string} nuevoEstado - El nuevo estado de la factura (ej. 'Pagada', 'Pendiente').
 * @returns {Promise<object>} La respuesta de la API.
 */
export const gestionarCobro = async (idFactura, nuevoEstado) => {
  try {
    const response = await apiClient.post(`/gestionar_cobro.php`, { id_factura: idFactura, nuevo_estado: nuevoEstado });
    return response.data;
  } catch (error) {
    console.error('Error al gestionar el cobro de la factura:', error);
    throw error;
  }
};

/**
 * Obtiene una lista de técnicos.
 * @returns {Promise<object>} La respuesta de la API.
 */
export const obtenerTecnicos = async () => {
  try {
    const response = await apiClient.get(`/obtener_tecnicos.php`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener técnicos:', error);
    throw error;
  }
};

export const obtenerTecnicosOperativa = async () => {
  try {
    const response = await apiClient.get('/obtener_tecnicos.php');
    return response.data;
  } catch (error) {
    console.error('Error al obtener técnicos de taller:', error);
    throw error;
  }
};

export const gestionarTecnicos = async (config = {}) => {
  try {
    const response = await apiClient.request({
      url: '/gestionar_tecnicos.php',
      ...config,
    });
    return response.data;
  } catch (error) {
    console.error('Error al gestionar técnicos:', error);
    throw error;
  }
};

export const obtenerMaterialesAviso = async (avisoId) => {
  const response = await apiClient.get('/obtener_materiales.php', { params: { aviso_id: avisoId } });
  return response.data;
};

export const guardarMaterialAviso = async (materialData) => {
  const response = await apiClient.post('/guardar_materiales.php', materialData);
  return response.data;
};

export const obtenerDashboardKpis = async () => {
  const response = await apiClient.get('/obtener_dashboard_kpis.php');
  return response.data;
};

export const obtenerMetricasFinancieras = async (filtros = {}) => {
  const response = await apiClient.get('/obtener_metricas_financieras.php', { params: filtros });
  return response.data;
};

export const exportarAvisosCsv = async (filtros = {}) => {
  const response = await apiClient.get('/exportar_avisos_csv.php', {
    params: filtros,
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'historial_avisos.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Registra un nuevo técnico (usuario con rol 'tecnico').
 * @param {object} userData - Los datos del técnico a registrar (nombre_usuario, correo_electronico, contrasena).
 * @returns {Promise<object>} La respuesta de la API.
 */
export const registrarTecnico = async (userData) => {
  try {
    const response = await apiClient.post(`/registro.php`, userData);
    return response.data;
  } catch (error) {
    console.error('Error al registrar técnico:', error);
    throw error;
  }
};